import { ArrowLeft, Activity, Shield, Lock, Eye, Database } from 'lucide-react';
import { motion } from 'framer-motion';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export default function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-gray-900">AgeQ AI</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 mb-8">Last updated: December 12, 2024</p>

          {/* Privacy Commitment */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Our Commitment to Your Privacy</h3>
            <p className="text-gray-700 leading-relaxed">
              At AgeQ AI, your privacy is paramount. We are committed to protecting your personal and health information. This policy explains how we collect, use, store, and protect your data.
            </p>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-cyan-600" />
                1. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.1 Account Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Email address</li>
                <li>Name</li>
                <li>Password (encrypted)</li>
                <li>Profile information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.2 Health and Biometric Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                To provide our services, we collect and process:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Age, gender, height, and weight</li>
                <li>Food intake data and nutritional information from scans</li>
                <li>Activity levels and exercise data</li>
                <li>Sleep patterns and quality metrics</li>
                <li>Biometric measurements you manually enter or sync from devices</li>
                <li>Photos of food items you scan (processed and not permanently stored)</li>
                <li>Health goals and preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.3 Usage Data</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>App usage patterns and features accessed</li>
                <li>Device information (type, operating system, browser)</li>
                <li>IP address and general location data</li>
                <li>Log data and analytics</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-cyan-600" />
                2. How We Use Your Information
              </h2>

              <p className="text-gray-700 leading-relaxed mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide and improve our health tracking and analysis services</li>
                <li>Calculate your biological age estimates and health metrics</li>
                <li>Generate personalized wellness recommendations and protocols</li>
                <li>Analyze food images and provide nutritional insights</li>
                <li>Send you notifications about your health progress and reminders</li>
                <li>Communicate important updates about the service</li>
                <li>Provide customer support</li>
                <li>Conduct research to improve our AI algorithms (anonymized data only)</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-cyan-600" />
                3. How We Protect Your Data
              </h2>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                <p className="text-gray-900 font-semibold mb-2">Security Measures</p>
                <p className="text-gray-700 leading-relaxed">
                  We implement industry-standard security measures to protect your data from unauthorized access, alteration, disclosure, or destruction.
                </p>
              </div>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>End-to-end encryption for sensitive health data</li>
                <li>Secure HTTPS connections for all data transmission</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication measures</li>
                <li>Encrypted database storage</li>
                <li>Regular backups with encryption</li>
                <li>Employee training on data privacy and security</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 We DO NOT Sell Your Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4 font-medium">
                We never sell your personal or health information to third parties. Your data is yours.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Limited Sharing</h3>
              <p className="text-gray-700 leading-relaxed mb-4">We may share your information only in these specific cases:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> Trusted third-party services that help us operate the App (cloud hosting, analytics, customer support)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with user notification)</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your information for as long as your account is active or as needed to provide you services. Specific retention policies:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Account Data:</strong> Retained until you delete your account</li>
                <li><strong>Health Metrics:</strong> Retained to provide historical tracking and trends</li>
                <li><strong>Food Scan Images:</strong> Processed immediately and not permanently stored</li>
                <li><strong>Usage Logs:</strong> Retained for 90 days for security and troubleshooting</li>
                <li><strong>Deleted Account Data:</strong> Permanently removed within 30 days of deletion request</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You have the following rights regarding your data:</p>

              <div className="space-y-4">
                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Access and Portability</h4>
                  <p className="text-gray-700">Request a copy of all your personal data in a portable format</p>
                </div>

                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Correction</h4>
                  <p className="text-gray-700">Update or correct inaccurate information through your account settings</p>
                </div>

                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Deletion</h4>
                  <p className="text-gray-700">Request deletion of your account and all associated data</p>
                </div>

                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Opt-Out</h4>
                  <p className="text-gray-700">Unsubscribe from marketing communications at any time</p>
                </div>

                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Restrict Processing</h4>
                  <p className="text-gray-700">Limit how we process your data in certain circumstances</p>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable laws.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                AgeQ AI is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected data from a child under 18, we will take steps to delete that information.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Essential Cookies:</strong> Required for the App to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the App</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings, but some features may not function properly if you disable them.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. GDPR Compliance (European Users)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Right to object to processing</li>
                <li>Right to withdraw consent at any time</li>
                <li>Right to lodge a complaint with your supervisory authority</li>
                <li>Right to data portability</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 space-y-2">
                <p className="text-gray-900"><strong>Email:</strong> care@ageq.app</p>
                <p className="text-gray-900"><strong>Data Protection Officer:</strong> care@ageq.app</p>
                <p className="text-gray-900"><strong>Response Time:</strong> We aim to respond within 48 hours</p>
              </div>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <p className="text-center text-gray-500 text-sm">
            &copy; 2025 AgeQ AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
